import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArchiveRestore } from 'lucide-react';

import PageHeader from '../../../components/page/PageHeader';
import { extractWikiPages, useWikiV2Client } from '../../../hooks/useWikiV2';
import WikiFrame from '../shared/WikiFrame';
import WikiIcon from '../shared/WikiIcon';
import { WikiEmpty, WikiError, WikiLoading, WikiNoAccess } from '../shared/WikiStates';
import useWikiRole from '../shared/useWikiRole';
import { invalidateWiki, useWikiQuery, wikiKeys } from '../shared/useWikiQueries';
import { dateLabel } from '../shared/wikiFormat';
import { wikiUi } from '../shared/wikiUi';
import { WIKI_MODAL_TOASTS } from '../modals/wikiModalCopy';
import { wikiModalErrorText, wikiPlanRefusal } from '../modals/wikiModalErrors';
import { WIKI_ARCHIVED_COPY as COPY } from './wikiArchivedCopy';
import { archivedCategoryRows, pageLocationLabel } from './wikiArchivedModel';
import './wikiArchived.css';

const enc = encodeURIComponent;

function restoreError(err) {
    const refusal = wikiPlanRefusal(err);
    return refusal ? refusal.message : wikiModalErrorText(err);
}

/** One Restore button + its inline error, per row. */
function useRowRestore() {
    const client = useWikiV2Client();
    const queryClient = useQueryClient();
    const [busyId, setBusyId] = useState(null);
    const [errors, setErrors] = useState({});

    const restore = async (id, path, toast) => {
        if (busyId) return;
        setBusyId(id);
        setErrors((prev) => ({ ...prev, [id]: '' }));
        try {
            await client.post(path, {});
            wikiUi.toast(toast);
            await invalidateWiki(queryClient);
        } catch (err) {
            setErrors((prev) => ({ ...prev, [id]: restoreError(err) }));
        } finally {
            setBusyId(null);
        }
    };
    return { busyId, errors, restore };
}

function RestoreButton({ id, busyId, onClick, testId }) {
    const busy = busyId === id;
    return (
        <button
            type="button"
            className="wk-btn wk-btn--secondary wk-btn--sm"
            aria-busy={busy ? 'true' : undefined}
            disabled={Boolean(busyId)}
            onClick={onClick}
            data-action="wiki.restore"
            data-testid={testId}
        >
            <WikiIcon name={ArchiveRestore} size={13} />
            {busy ? COPY.restoring : COPY.restore}
        </button>
    );
}

function RowError({ message, testId }) {
    if (!message) return null;
    return (
        <p className="wk-arch-row-error" role="alert" data-testid={testId}>
            <WikiIcon name="alert-triangle" size={13} />
            <span>{message}</span>
        </p>
    );
}

function Section({ title, query, empty, children, testId }) {
    let body;
    if (query.isLoading) body = <WikiLoading label={COPY.loading} />;
    else if (query.isError) body = <WikiError error={query.error} onRetry={() => query.refetch()} />;
    else if (!children) body = <WikiEmpty icon="folder" title={empty} />;
    else body = <ul className="wk-arch-list">{children}</ul>;
    return (
        <section className="wk-arch-section" data-testid={testId}>
            <h2 className="wk-t-label wk-arch-title">{title}</h2>
            <div className="wk-card wk-card--flush">{body}</div>
        </section>
    );
}

/**
 * Archived — `/wiki/archived` (#872). The one place to find and restore what
 * was archived:
 *   Archived categories  Wiki admins (`GET /wiki/categories?include_archived=1`,
 *                        wiki:admin + MFA). A subcategory whose parent is also
 *                        archived sits under its parent, without a button.
 *   Archived pages       editors and admins (`GET /wiki/pages?status=archived`,
 *                        only pages the caller can restore). A page whose
 *                        category is archived (`location_archived`) gets no
 *                        button either; it says what to do instead.
 * Members get the no-access card. Restore refreshes every Wiki query; a
 * refusal (plan limit, parent archived, MFA) stays on its row.
 */
export default function WikiArchived() {
    const role = useWikiRole();
    const categoriesQuery = useWikiQuery(
        wikiKeys.archived('categories'),
        '/wiki/categories?include_archived=1',
        { enabled: role.canAdmin },
    );
    const pagesQuery = useWikiQuery(
        wikiKeys.archived('pages'),
        '/wiki/pages?status=archived&limit=500',
        { enabled: role.canEdit },
    );
    const { busyId, errors, restore } = useRowRestore();

    if (!role.canEdit) {
        return (
            <WikiFrame testId="wiki-archived">
                <PageHeader title={COPY.title} />
                <div className="wk-card"><WikiNoAccess title={COPY.noAccessTitle} detail={COPY.noAccessBody} /></div>
            </WikiFrame>
        );
    }

    const categoryRows = role.canAdmin ? archivedCategoryRows(categoriesQuery.data) : [];
    const pages = extractWikiPages(pagesQuery.data);

    return (
        <WikiFrame testId="wiki-archived">
            <PageHeader
                title={COPY.title}
                subtitle={role.canAdmin ? COPY.subtitleAdmin : COPY.subtitleEditor}
                testId="wiki-archived-header"
            />

            {role.canAdmin && (
                <Section
                    title={COPY.categoriesTitle}
                    query={categoriesQuery}
                    empty={COPY.categoriesEmpty}
                    testId="wiki-archived-categories"
                >
                    {categoryRows.length > 0 && categoryRows.map((row) => (
                        <li key={row.id} className="wk-arch-row" data-testid={`wiki-archived-category-${row.id}`}>
                            <div className="wk-arch-row-main">
                                <WikiIcon name="folder" size={16} className="wk-arch-row-icon" />
                                <div className="wk-arch-row-text">
                                    <span className="wk-t-row">{row.name}</span>
                                    <span className="wk-t-meta">
                                        {[row.parentName ? COPY.insideOf(row.parentName) : null, COPY.archivedOn(dateLabel(row.archivedAt))]
                                            .filter(Boolean).join(' · ')}
                                    </span>
                                    {row.inside.map((sub) => (
                                        <span key={sub.id} className="wk-t-meta wk-arch-inside" data-testid={`wiki-archived-inside-${sub.id}`}>
                                            {COPY.insideArchived(sub.name, row.name)}
                                        </span>
                                    ))}
                                    <RowError message={errors[row.id]} testId={`wiki-archived-error-${row.id}`} />
                                </div>
                            </div>
                            <RestoreButton
                                id={row.id}
                                busyId={busyId}
                                testId={`wiki-restore-category-${row.id}`}
                                onClick={() => restore(row.id, `/wiki/categories/${enc(row.id)}/restore`, WIKI_MODAL_TOASTS.categoryRestored(row.name))}
                            />
                        </li>
                    ))}
                </Section>
            )}

            <Section
                title={COPY.pagesTitle}
                query={pagesQuery}
                empty={COPY.pagesEmpty}
                testId="wiki-archived-pages"
            >
                {pages.length > 0 && pages.map((page) => {
                    const where = pageLocationLabel(page);
                    const stranded = page.location_archived === true;
                    return (
                        <li key={page.page_id} className="wk-arch-row" data-testid={`wiki-archived-page-${page.page_id}`}>
                            <div className="wk-arch-row-main">
                                <WikiIcon name="file-text" size={16} className="wk-arch-row-icon" />
                                <div className="wk-arch-row-text">
                                    <span className="wk-t-row">{page.title || 'Untitled page'}</span>
                                    <span className="wk-t-meta">
                                        {[where, COPY.archivedOn(dateLabel(page.updated_at))].filter(Boolean).join(' · ')}
                                    </span>
                                    {stranded && (
                                        <span className="wk-t-meta wk-arch-inside" data-testid={`wiki-archived-stranded-${page.page_id}`}>
                                            {role.canAdmin ? COPY.restoreLocationFirst(where) : COPY.askAdminLocation}
                                        </span>
                                    )}
                                    <RowError message={errors[page.page_id]} testId={`wiki-archived-error-${page.page_id}`} />
                                </div>
                            </div>
                            {!stranded && (
                                <RestoreButton
                                    id={page.page_id}
                                    busyId={busyId}
                                    testId={`wiki-restore-page-${page.page_id}`}
                                    onClick={() => restore(page.page_id, `/wiki/pages/${enc(page.page_id)}/restore`, WIKI_MODAL_TOASTS.pageRestored(page.title || 'The page'))}
                                />
                            )}
                        </li>
                    );
                })}
            </Section>
        </WikiFrame>
    );
}
